import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useTournamentDetail } from '../../../hooks/useTournaments';
import Sidebar from '../../admin-dashboard/components/Sidebar';

/**
 * Admin V2 Tournament Detail — participants + bracket (matches groupés par round).
 * Route : /admin/tournaments/:id + /admin/v2/tournaments/:id.
 * Read-only display (création de matches/participants = itération future).
 */
const AdminV2TournamentDetail = ({ backPath = '/admin/tournaments' }) => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const { id } = useParams();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { tournament, participants, matches, loading, error } = useTournamentDetail(id);

  const pName = (pid) => {
    const p = participants.find((x) => x.id === pid);
    return p?.display_name || (p?.creator_id ? p.creator_id.slice(0, 8) : '—');
  };

  const rounds = matches.reduce((acc, m) => { (acc[m.round] = acc[m.round] || []).push(m); return acc; }, {});

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />
      <main className="flex-1 min-w-0 overflow-x-hidden">
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center gap-3">
            <button type="button" onClick={() => navigate(backPath)} className="text-muted-foreground hover:text-foreground text-sm">←</button>
            <div className="min-w-0">
              <Link to={backPath} className="text-xs text-muted-foreground hover:text-foreground">{t('tournamentsV2.title', 'Tournaments')}</Link>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground truncate">{tournament?.title || '—'}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">{tenantName}{tournament ? ` · ${tournament.format} · ${tournament.server || '—'}` : ''}</p>
            </div>
          </div>
        </header>
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {error && <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error">{String(error.message || error)}</div>}
          {loading && !tournament ? (
            <div className="rounded-2xl bg-card border border-border h-48 skeleton" />
          ) : !tournament ? (
            <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">{t('tournamentsV2.notFound', 'Tournament not found.')}</div>
          ) : (
            <>
              <section className="rounded-2xl bg-card border border-border overflow-hidden">
                <div className="px-5 py-3 border-b border-border bg-surface-2 font-heading font-semibold text-foreground text-sm">
                  {t('tournamentsV2.participants', 'Participants')} ({participants.length})
                </div>
                {participants.length === 0 ? (
                  <p className="px-5 py-8 text-center text-sm text-muted-foreground">{t('tournamentsV2.noParticipants', 'No participants registered.')}</p>
                ) : (
                  <ul className="divide-y divide-border">
                    {participants.map((p) => (
                      <li key={p.id} className="px-5 py-2.5 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 min-w-0">
                          {p.seed != null && <span className="text-xs text-muted-foreground tabular-nums w-6">#{p.seed}</span>}
                          <span className="font-medium text-foreground truncate">{p.display_name || (p.creator_id ? p.creator_id.slice(0, 8) : '—')}</span>
                        </div>
                        <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{p.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="space-y-3">
                <div className="text-xs uppercase tracking-widest text-muted-foreground/70 font-semibold px-1">
                  {t('tournamentsV2.bracket', 'Bracket / matches')}
                </div>
                {matches.length === 0 ? (
                  <div className="rounded-2xl bg-card border border-border p-8 text-center text-sm text-muted-foreground">{t('tournamentsV2.noMatches', 'No matches scheduled.')}</div>
                ) : (
                  Object.entries(rounds).map(([round, list]) => (
                    <div key={round} className="rounded-2xl bg-card border border-border overflow-hidden">
                      <div className="px-5 py-2 border-b border-border bg-surface-2 text-xs font-semibold text-foreground">
                        {t('tournamentsV2.round', 'Round')} {round}
                      </div>
                      <ul className="divide-y divide-border">
                        {list.map((m) => (
                          <li key={m.id} className="px-5 py-3 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <span className={`truncate ${m.winner === m.participant_a ? 'font-bold text-success' : 'text-foreground'}`}>{pName(m.participant_a)}</span>
                              <span className="text-xs text-muted-foreground tabular-nums">{m.score_a ?? '–'} : {m.score_b ?? '–'}</span>
                              <span className={`truncate ${m.winner === m.participant_b ? 'font-bold text-success' : 'text-foreground'}`}>{pName(m.participant_b)}</span>
                            </div>
                            <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-muted text-muted-foreground shrink-0">{m.status}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
                )}
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminV2TournamentDetail;
